import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../redux/hooks';
import { hasPermission } from '../utils/permissionUtils';
import toast from 'react-hot-toast';

interface ProtectedRouteProps {
  permissionKey: string;
  fallbackPath: string;
  children: React.ReactNode;
}

/**
 * A component that protects routes based on user permissions.
 * It checks if the user has the required permission to access the route.
 * If not, it redirects them to the specified fallback path with an error message.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  permissionKey, 
  fallbackPath, 
  children 
}) => {
  const { user } = useAppSelector(state => state.auth);
  const location = useLocation();

  // Check if user has the required permission
  const hasRequiredPermission = hasPermission(user, permissionKey);

  // Show error toast if user doesn't have permission
  useEffect(() => {
    if (!hasRequiredPermission) {
      toast.error(`You don't have permission to access this page`);
    }
  }, [hasRequiredPermission]);

  // If user is not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // If user doesn't have the required permission, redirect to fallback path
  if (!hasRequiredPermission) {
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // If user has permission, render the children
  return <>{children}</>;
};

export default ProtectedRoute; 
